import { inject, Injectable, InjectionToken } from '@angular/core';

export interface FilePickerOptions {
  acceptedTypes?: string;
  multiple?: boolean;
  /** webkitdirectory: the picker hands back every file inside the chosen folder, not the folder itself. */
  directory?: boolean;
}

export interface FilePickerSilentClose {
  accept: string;
}

/** `reason` — the DOMException name the engine refused `showPicker()` with. */
export interface FilePickerRefused {
  accept: string;
  reason: string;
}

export interface FilePickerTelemetry {
  silentClose(context: FilePickerSilentClose): void;
  pickerRefused(context: FilePickerRefused): void;
}

/** Reports a picker session that ended with neither `change` nor `cancel` — the silent loss this service exists to
 *  prevent (FKS-10841). Optional: with no provider the picker just works, unreported. */
export const FILE_PICKER_TELEMETRY = new InjectionToken<FilePickerTelemetry>('FILE_PICKER_TELEMETRY');

/** The window is back and the OS dialog is gone: past this, a `change` that never arrived is a loss, not latency.
 *  Generous on purpose — cloud-backed photos (iCloud) materialize for seconds after the dialog closes, and a false
 *  «loss» would send us chasing a bug that is already fixed; a real loss just reports a little later. */
const SILENT_CLOSE_GRACE_MS = 10_000;

/**
 * Сервис для программного открытия диалога выбора файлов.
 *
 * The input MUST live in the document while the OS dialog is open: a detached node is reachable only through its own
 * handler, so WebKit is free to collect it mid-dialog and the picked files never arrive (FKS-10841 — Safari dropped
 * the first pick, a retry worked). Every file-picking surface goes through here; a raw `document.createElement('input')`
 * outside this file is banned by lint.
 */
@Injectable({ providedIn: 'root' })
export class FilePickerService {
  private readonly telemetry = inject(FILE_PICKER_TELEMETRY, { optional: true });

  /**
   * Открывает системный диалог выбора файлов
   * @returns Promise с массивом выбранных файлов (пустой массив если отменено)
   */
  open(options: FilePickerOptions): Promise<File[]> {
    const { acceptedTypes = '', multiple = true, directory = false } = options;

    return new Promise<File[]>(resolve => {
      // eslint-disable-next-line no-restricted-syntax -- the one sanctioned home of the file input; the guard points here
      const input = document.createElement('input');
      input.type = 'file';
      if (acceptedTypes) input.accept = acceptedTypes;
      input.multiple = multiple;
      if (directory) input.webkitdirectory = true;
      input.style.display = 'none';

      let settled = false;
      let graceTimer: ReturnType<typeof setTimeout> | null = null;

      const onWindowFocus = (): void => {
        if (settled || graceTimer !== null) return;
        graceTimer = setTimeout(() => {
          // Deliberately neither resolved nor removed: a late `change` still delivers the files, and pulling the node
          // out of the document now would recreate the very detachment this service guards against.
          if (!settled) this.telemetry?.silentClose({ accept: acceptedTypes });
        }, SILENT_CLOSE_GRACE_MS);
      };

      const settle = (files: File[]): void => {
        settled = true;
        window.removeEventListener('focus', onWindowFocus);
        if (graceTimer !== null) clearTimeout(graceTimer);
        input.remove();
        resolve(files);
      };

      input.onchange = (): void => settle(input.files ? Array.from(input.files) : []);
      input.oncancel = (): void => settle([]);

      // No watchdog for folder picks: Chrome inserts a tab-modal «Upload N files?» confirm AFTER the OS dialog closes
      // (window refocuses) and BEFORE `change` fires — the user can sit on it indefinitely, so «no change after
      // refocus» is not a loss signal there.
      if (!directory) window.addEventListener('focus', onWindowFocus);
      document.body.appendChild(input);
      this.openDialog(input, acceptedTypes);
    });
  }

  private openDialog(input: HTMLInputElement, accept: string): void {
    // The input lives in `document.body`, outside every CDK overlay: a synthetic click bubbles up and the open
    // dialog reads it as a backdrop click, dismissing the very surface that asked for the file. `showPicker()`
    // opens the same OS dialog without dispatching anything.
    if (typeof input.showPicker !== 'function') {
      input.click();
      return;
    }

    try {
      input.showPicker();
    } catch (error) {
      // The fallback is that dismissing click, and a refusal blocks it too — the pick is lost with nothing on screen.
      this.telemetry?.pickerRefused({ accept, reason: error instanceof DOMException ? error.name : 'unknown' });
      input.click();
    }
  }
}
